import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { components } from 'react-select';

const BADGE_WIDTH = 40;
const CHIP_GAP = 4;

export const MultiValueComponent = (props: any) => {
  return (
    <components.MultiValue key={props.data.value.toString()} {...props}>
      <span style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>{props.data.label}</span>
    </components.MultiValue>
  );
};

const isChildType = (child: any, name: string) =>
  child?.type?.displayName === name || child?.type?.name === name;

export const ValueContainerWithOverflowTip = (props: any) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const menuIsOpen: boolean = props.selectProps.menuIsOpen;

  const childrenArray = React.Children.toArray(props.children);
  const inputChild = childrenArray.find((c: any) => isChildType(c, 'Input'));
  const placeholderChild = childrenArray.find((c: any) => isChildType(c, 'Placeholder'));
  const chips = childrenArray.filter((c: any) => !isChildType(c, 'Input') && !isChildType(c, 'Placeholder'));

  const total = chips.length;
  const hidden = visibleCount !== null ? total - visibleCount : 0;

  const getAvailableWidth = () => {
    const control = containerRef.current?.closest('.accSelector__control') as HTMLElement | null;
    if (!control) return containerRef.current?.offsetWidth || 0;
    const controlStyle = getComputedStyle(control);
    const controlPadding = parseFloat(controlStyle.paddingLeft) + parseFloat(controlStyle.paddingRight);
    // Measure siblings of containerRef (indicators: clear X, dropdown arrow, etc.)
    let siblingsWidth = 0;
    let sibling = containerRef.current?.nextElementSibling;
    while (sibling) {
      siblingsWidth += (sibling as HTMLElement).offsetWidth;
      sibling = sibling.nextElementSibling;
    }
    return control.offsetWidth - controlPadding - siblingsWidth;
  };

  const measure = () => {
    const measureEl = measureRef.current;
    if (!measureEl || total === 0) { setVisibleCount(null); return; }

    const tags = Array.from(measureEl.querySelectorAll<HTMLElement>('.accSelector__multi-value'));
    if (!tags.length) { setVisibleCount(null); return; }

    const available = getAvailableWidth();
    if (available <= 0) { setVisibleCount(null); return; }

    let fullWidth = 0;
    for (const tag of tags) fullWidth += tag.offsetWidth + CHIP_GAP;
    if (fullWidth <= available) { setVisibleCount(null); return; }

    let used = 0;
    let fits = 0;
    for (const tag of tags) {
      const w = tag.offsetWidth + CHIP_GAP;
      if (used + w + BADGE_WIDTH > available) break;
      used += w;
      fits++;
    }
    setVisibleCount(fits);
  };

  useLayoutEffect(() => { measure(); }, [total]);

  useEffect(() => {
    const wrapper = containerRef.current?.closest('.accSelector-outer-wrapper') as HTMLElement | null;
    const target = wrapper || containerRef.current;
    if (!target || total === 0) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(target);
    return () => observer.disconnect();
  }, [total]);

  useEffect(() => {
    if (menuIsOpen) containerRef.current?.querySelector('input')?.focus();
  }, [menuIsOpen]);

  const displayed = visibleCount !== null ? chips.slice(0, visibleCount) : chips;
  const allLabels = hidden > 0
    ? chips.map((c: any) => c?.props?.data?.label).filter(Boolean).join(', ')
    : '';

  return (
    <div ref={containerRef} style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
      <div ref={measureRef} style={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', display: 'flex', flexWrap: 'nowrap', pointerEvents: 'none' }}>
        {chips}
      </div>
      <components.ValueContainer {...props}>
        {total === 0 && placeholderChild}
        {displayed}
        {hidden > 0 && (
          <span title={allLabels} style={{ whiteSpace: 'nowrap', fontSize: '14px', color: 'var(--gray-500)', padding: '2px 4px', flexShrink: 0, cursor: 'default' }}>
            +{hidden}
          </span>
        )}
        {inputChild}
      </components.ValueContainer>
    </div>
  );
};
